import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { 
  LogOut, 
  Bookmark, 
  ChevronRight,
  Check
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PHASES = [
  {
    id: "clarity",
    phase: 1,
    name: "Clarity",
    subtitle: "Define Your Foundation",
    description: "Get crystal clear on your vision, ideal client, and core offer.",
    path: "/phase/clarity",
    days: "Days 1–3",
    steps: ["Vision Statement", "Ideal Client", "Core Offer", "Revenue Mapping"]
  },
  {
    id: "asset-builder",
    phase: 2,
    name: "Assets",
    subtitle: "Build Your Engine",
    description: "Create the strategic assets that attract and convert.",
    path: "/phase/assets",
    days: "Days 4–7",
    steps: ["Lead Magnet", "Content Pillars", "Email Sequence", "Product Outline"]
  },
  {
    id: "authority",
    phase: 3,
    name: "Authority",
    subtitle: "Establish Credibility",
    description: "Craft the materials that build trust and expertise.",
    path: "/phase/authority",
    days: "Days 8–10",
    steps: ["Bio Generator", "Testimonials", "Case Study", "Media Kit"]
  },
  {
    id: "launch",
    phase: 4,
    name: "Launch",
    subtitle: "Go Live Calmly",
    description: "Prepare for a sustainable, stress-free launch.",
    path: "/phase/launch",
    days: "Days 11–14",
    steps: ["Timeline", "Pricing", "Objections", "Launch Copy"]
  }
];

export default function Journey() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState({});

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/progress`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProgress(data.progress || {});
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  };

  const getInitials = (name) => {
    if (!name) return "CS";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getPhaseProgress = (phaseId) => {
    const phaseSteps = {
      'clarity': ['vision_statement', 'ideal_client', 'core_offer', 'revenue_mapping'],
      'asset-builder': ['lead_magnet', 'content_pillars', 'email_sequence', 'product_outline'],
      'authority': ['bio_generator', 'testimonial_framework', 'case_study', 'media_kit'],
      'launch': ['launch_timeline', 'pricing_strategy', 'objection_handler', 'launch_copy']
    };
    const steps = phaseSteps[phaseId] || [];
    const completed = steps.filter(s => progress[s]).length;
    return { completed, total: steps.length };
  };

  const getRecommendedNext = () => {
    for (const phase of PHASES) {
      const { completed, total } = getPhaseProgress(phase.id);
      if (completed < total) {
        return phase;
      }
    }
    return null;
  };

  const recommendedPhase = getRecommendedNext();

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/80 backdrop-blur-sm border-b border-[#E5E0D6]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/journey" className="flex items-center gap-2">
            <span className="text-lg text-[#2C2C2C]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Calm Scale™
            </span>
          </Link>
          
          <div className="flex items-center gap-4">
            <Link 
              to="/saved" 
              className="flex items-center gap-2 text-[#2C2C2C]/60 hover:text-[#6B8E7C] transition-colors"
              data-testid="saved-outputs-link"
            >
              <Bookmark className="w-4 h-4" />
              <span className="hidden sm:inline text-sm">Saved</span>
            </Link>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full" data-testid="user-menu-btn">
                  <Avatar className="h-9 w-9 border border-[#E5E0D6]">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-[#F2F0EB] text-[#2C2C2C] text-xs">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-white border-[#E5E0D6]" align="end">
                <div className="px-3 py-2 border-b border-[#E5E0D6]">
                  <p className="text-sm font-medium text-[#2C2C2C]">{user?.name}</p>
                  <p className="text-xs text-[#2C2C2C]/50">{user?.email}</p>
                </div>
                <DropdownMenuItem 
                  onClick={logout}
                  className="cursor-pointer text-[#2C2C2C]/70 hover:text-[#2C2C2C] focus:bg-[#F2F0EB]"
                  data-testid="logout-btn"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-16 animate-fade-up opacity-0" style={{ animationFillMode: 'forwards' }}>
            <p className="text-xs text-[#D4A373] font-medium tracking-widest uppercase mb-4">
              Your Blueprint
            </p>
            <h1 className="text-3xl md:text-4xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              The Calm Scale Method
            </h1>
            <p className="text-[#2C2C2C]/60 font-light">
              Four phases. Move through each at your own pace.
            </p>
          </div>

          {/* Recommended Next Step */}
          {recommendedPhase && (
            <div 
              className="mb-12 p-6 bg-[#F2F0EB]/50 border border-[#E5E0D6] rounded-xl animate-fade-up opacity-0"
              style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6B8E7C] uppercase tracking-wide font-medium mb-1">
                    Continue where you left off
                  </p>
                  <p className="text-lg text-[#2C2C2C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Phase {recommendedPhase.phase}: {recommendedPhase.name}
                  </p>
                </div>
                <Button
                  onClick={() => navigate(recommendedPhase.path)}
                  className="btn-primary text-sm px-6"
                  data-testid="continue-btn"
                >
                  {recommendedPhase.phase === 1 ? 'Start Phase One' : `Continue Phase ${recommendedPhase.phase}`}
                  <ChevronRight className="ml-1 w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Phases */}
          <div className="space-y-4">
            {PHASES.map((phase, index) => {
              const { completed, total } = getPhaseProgress(phase.id);
              const isComplete = completed === total && total > 0;
              
              return (
                <div
                  key={phase.id}
                  onClick={() => navigate(phase.path)}
                  className="group cursor-pointer p-6 bg-white border border-[#E5E0D6] rounded-xl hover:border-[#6B8E7C]/30 transition-colors animate-fade-up opacity-0"
                  style={{ animationDelay: `${(index + 2) * 100}ms`, animationFillMode: 'forwards' }}
                  data-testid={`phase-card-${phase.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      {/* Phase Number */}
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center text-sm flex-shrink-0
                        ${isComplete ? 'bg-[#6B8E7C] text-white' : 'bg-[#F2F0EB] text-[#2C2C2C]/50'}
                        transition-colors
                      `}>
                        {isComplete ? <Check className="w-4 h-4" /> : phase.phase}
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                            {phase.name}
                          </h3>
                          <span className="text-xs text-[#2C2C2C]/40">{phase.days}</span>
                        </div>
                        <p className="text-sm text-[#2C2C2C]/60 font-light">
                          {phase.description}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#E5E0D6] group-hover:text-[#6B8E7C] transition-colors flex-shrink-0 mt-2" />
                  </div>

                  {/* Progress */}
                  <div className="flex items-center gap-3 ml-14">
                    <div className="flex-1 h-1 bg-[#F2F0EB] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#6B8E7C] rounded-full transition-all duration-500"
                        style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="text-xs text-[#2C2C2C]/40 flex-shrink-0">
                      {completed}/{total}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer Note */}
          <div className="mt-16 text-center animate-fade-up opacity-0" style={{ animationDelay: '700ms', animationFillMode: 'forwards' }}>
            <p className="text-sm text-[#2C2C2C]/40 font-light italic">
              "Take your time. Clarity compounds."
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
